import { Link } from "react-router-dom";
import { useLanguage } from "@/i18n";

// Logo image - with transparent background (larger version for footer)
const LOGO_IMAGE = "https://customer-assets.emergentagent.com/job_export-to-wp/artifacts/lkrjqebc_Logo%20Spurna.png";

// Social media icons image (same as Hablemos page)
const SOCIAL_ICONS_IMAGE = "https://customer-assets.emergentagent.com/job_6755e027-e15f-4d08-a33d-914c47bd2f3e/artifacts/n7izc9wn_xarxes.png";

// Social media links (identical to Hablemos page)
const socialLinks = [
  { name: "LinkedIn", url: "https://www.linkedin.com/company/spurna-creativa/", position: { left: "0%", width: "16.66%" } },
  { name: "YouTube", url: "https://www.youtube.com/@SpurnaCreativa", position: { left: "16.66%", width: "16.66%" } },
  { name: "Instagram", url: "https://www.instagram.com/spurnacreativa/", position: { left: "33.33%", width: "16.66%" } },
  { name: "TikTok", url: "https://www.tiktok.com/@spurnacreativa", position: { left: "50%", width: "16.66%" } },
  { name: "X", url: "https://x.com/SpurnaCFilms", position: { left: "66.66%", width: "16.66%" } },
  { name: "Facebook", url: "https://www.facebook.com/profile.php?id=61576228404607", position: { left: "83.33%", width: "16.66%" } },
];

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t, isEnglish } = useLanguage();
  
  // Get correct paths based on language
  const paths = isEnglish ? {
    home: "/en",
    filosofia: "/en/philosophy",
    proyectos: "/en/projects",
    publicidad: "/en/advertising",
    queHacemos: "/en/what-we-do",
    hablemos: "/en/contact",
    legal: "/en/legal",
  } : {
    home: "/",
    filosofia: "/filosofia",
    proyectos: "/proyectos",
    publicidad: "/publicidad",
    queHacemos: "/que-hacemos",
    hablemos: "/hablemos",
    legal: "/legal",
  };
  
  // Navigation labels based on language
  const navLabels = isEnglish ? {
    filosofia: "Philosophy",
    proyectos: "Projects",
    publicidad: "Advertising",
    queHacemos: "What we do",
    hablemos: "Let's Talk",
    legal: "Legal",
  } : {
    filosofia: "Filosofía",
    proyectos: "Proyectos",
    publicidad: "Publicidad",
    queHacemos: "Qué hacemos",
    hablemos: "Hablemos",
    legal: "Legal",
  };

  return (
    <footer className="bg-background border-t border-border/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12 lg:py-16">
        
        {/* Social Media Icons - Same as Hablemos page (shown on all pages incl. Home) */}
          <div className="flex justify-center mb-12 lg:mb-16" data-testid="footer-social-icons">
            <div className="relative w-full max-w-[600px]">
              <img
                src={SOCIAL_ICONS_IMAGE}
                alt="Social Media Icons"
                className="w-full h-auto"
              />
              {/* Clickable overlay areas for each social icon */}
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute top-0 bottom-0 hover:bg-white/5 transition-colors"
                  style={{
                    left: social.position.left,
                    width: social.position.width,
                  }}
                  aria-label={social.name}
                  data-testid={`footer-social-link-${social.name.toLowerCase()}`}
                />
              ))}
            </div>
          </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-16">
          {/* Brand - Logo + Quote */}
          <div className="space-y-6">
            <Link to={paths.home} className="inline-block">
              <img
                src={LOGO_IMAGE}
                alt="Spurna Creativa Logo"
                className="h-16 sm:h-18 lg:h-24 w-auto object-contain"
                style={{ 
                  maxWidth: '240px',
                  filter: 'brightness(0.85) contrast(1.1) sepia(0.1)',
                  opacity: 0.7,
                }}
              />
            </Link>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t.footer.quote}
              </p>
              <p className="text-sm text-muted-foreground/70 italic">
                {t.footer.quoteAuthor}
              </p>
            </div>
          </div>

          {/* Navigation */}
          <div className="space-y-4">
            <h4 className="text-xs font-medium tracking-[0.2em] uppercase text-foreground/50">
              {t.footer.navigation}
            </h4>
            <nav className="flex flex-col gap-2">
              <Link 
                to={paths.filosofia} 
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {navLabels.filosofia}
              </Link>
              <Link 
                to={paths.proyectos} 
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {navLabels.proyectos}
              </Link>
              <Link 
                to={paths.publicidad} 
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {navLabels.publicidad}
              </Link>
              <Link 
                to={paths.queHacemos} 
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {navLabels.queHacemos}
              </Link>
              <Link 
                to={paths.hablemos} 
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {navLabels.hablemos}
              </Link>
              <Link 
                to={paths.legal} 
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {navLabels.legal}
              </Link>
            </nav>
          </div>

          {/* Contact - Emails + Phone numbers with "/" separator */}
          <div className="space-y-4">
            <h4 className="text-xs font-medium tracking-[0.2em] uppercase text-foreground/50">
              {t.footer.contact}
            </h4>
            <div className="space-y-2">
              <a 
                href="mailto:emma@spurnacreativa.com" 
                className="block text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                emma@spurnacreativa.com
              </a>
              <a 
                href="mailto:roger@spurnacreativa.com" 
                className="block text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                roger@spurnacreativa.com
              </a>
              <p className="text-sm text-muted-foreground pt-2">
                +34 617 48 10 95 / +34 650 49 23 68
              </p>
            </div>
          </div>
        </div>

        {/* Bottom bar - Copyright only */}
        <div className="mt-12 pt-8 border-t border-border/10">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-xs text-muted-foreground/60">
              {currentYear} Spurna Creativa. {t.footer.copyright}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
